const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 4;

/** Below this much free space the panel flips above the trigger, if there is more room there. */
const FLIP_THRESHOLD = 280;

/**
 * Compute a fixed-position style for a panel anchored to a trigger element, kept inside the
 * viewport. Opens below the trigger and flips above when space runs out; the returned maxHeight
 * caps the panel so it never runs off screen.
 *
 * Returns `left` only when a width is known — a panel that is auto-sized (align 'stretch') is
 * positioned by `left` + `minWidth` instead, since it cannot be clamped before it has measured.
 *
 * @param {DOMRect} rect - the trigger's bounding rect
 * @param {number|null} width - rendered panel width in px, or null when it is not yet measured
 * @param {'start'|'end'|'center'|'stretch'} align - how the panel lines up with the trigger
 * @returns {{ left: number, top?: number, bottom?: number, maxHeight: number, minWidth?: number }}
 */
export function computeAnchorPosition(rect, width, align = 'start') {
	const vw = window.innerWidth;
	const vh = window.innerHeight;

	const base = {
		start: rect.left,
		end: rect.right - (width ?? rect.width),
		center: rect.left + rect.width / 2 - (width ?? rect.width) / 2,
		stretch: rect.left,
	};

	let left = base[align] ?? rect.left;
	if (width)
		left = Math.min(Math.max(VIEWPORT_MARGIN, left), Math.max(VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN));

	const spaceBelow = vh - rect.bottom - VIEWPORT_MARGIN - TRIGGER_GAP;
	const spaceAbove = rect.top - VIEWPORT_MARGIN - TRIGGER_GAP;
	const openUp = spaceBelow < FLIP_THRESHOLD && spaceAbove > spaceBelow;

	const vertical = openUp
		? { bottom: vh - rect.top + TRIGGER_GAP, maxHeight: spaceAbove }
		: { top: rect.bottom + TRIGGER_GAP, maxHeight: spaceBelow };

	return {
		left,
		...vertical,
		...(align === 'stretch' ? { minWidth: rect.width } : {}),
	};
}
